import { type ButtonHTMLAttributes, forwardRef } from 'react';
import { cn } from '@/lib/utils';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'subtle';
type Size = 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
}

// Primary uses a fixed near-black ink on warm amber for consistent contrast in
// both themes (cream-bg `--bg` would not have enough contrast against amber).
const variantClasses: Record<Variant, string> = {
  primary:   'bg-accent text-[rgb(20_15_8)] hover:bg-accent/90 active:scale-[0.98] shadow-[0_1px_0_0_rgb(255_255_255/0.18)_inset,0_2px_8px_-2px_rgb(var(--accent)/0.4)]',
  secondary: 'bg-bg-card text-fg border border-border hover:border-border-strong hover:bg-bg-elevated',
  ghost:     'text-fg hover:bg-bg-elevated',
  danger:    'bg-bear/15 text-bear hover:bg-bear/25 border border-bear/30',
  subtle:    'bg-bg-elevated text-fg-muted hover:text-fg hover:bg-bg-card',
};
const sizeClasses: Record<Size, string> = {
  sm: 'h-8 px-3 text-xs',
  md: 'h-10 px-4 text-sm',
  lg: 'h-12 px-6 text-base',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', loading, className, children, disabled, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      disabled={disabled || loading}
      className={cn(
        'inline-flex items-center justify-center gap-2 rounded-lg font-semibold',
        'transition-all duration-150 ease-out-expo',
        'disabled:opacity-50 disabled:pointer-events-none',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60',
        variantClasses[variant],
        sizeClasses[size],
        className,
      )}
      {...rest}
    >
      {loading && (
        <svg className="size-4 animate-spin" viewBox="0 0 24 24" fill="none">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8v3a5 5 0 0 0-5 5H4z" />
        </svg>
      )}
      {children}
    </button>
  );
});
